/**
 * ロビー（自前・GPL非依存）。在席プレゼンス＋自動キュー型マッチメイク＋観戦。
 *
 * MsgChannel（ルーム単位の再ブロードキャスト・エコーなし）の上に構築するため、
 * チャンネル実装を差し替えるだけでローカル検証（BroadcastChannel・同一ブラウザ多タブ）と
 * 遠隔（PartyKit/WebSocket）の両方で同一ロジックが動く。
 *
 * サーバーは状態を持たない（純粋リレー）。ロスターに居る最小 userId のクライアントが
 * 「集約役（arbiter）」となり待機者を先着順でペアリングする（決定的なので競合しない）。
 * 在席はハートビート＋一定時間未受信で失効（タブ/接続が消えれば自然に消える）。
 */

import type {
  LobbyClient,
  LobbyUser,
  Roster,
  MatchAssignment,
  AuthResult,
  AuthStatus,
  MatchStats,
  LeaderboardEntry,
  AdminResult,
  AdminUser,
  AdminPatch,
  ShotRecord,
  AnalyticsResponse,
  AnalyticsTotals,
  AnalyticsBucket,
  RaceConfig,
  RaceProposal,
  RaceUpdated,
  RaceStartInfo,
  RaceAborted,
  PickFailReason,
  CpuDefeatEvent,
  DrillClearEvent,
  InventorySnapshot,
  InventoryUpdate,
} from './lobby.js';
import type { ChannelFactory, MsgChannel } from './channel.js';
import { broadcastChannelFactory } from './channel.js';
import { isCpuUserId, lobbyCpuPlayers } from '../ai/cpu-players.js';

type LobbyMsg =
  | { readonly t: 'presence'; readonly user: LobbyUser }
  | { readonly t: 'bye'; readonly userId: string }
  | {
      readonly t: 'matched';
      readonly matchId: string;
      readonly hostId: string;
      readonly guestId: string;
    }
  // 簡易アカウント認証（サーバー権威・pool-lobby ルームのみ・設計 §13）。
  | { readonly t: 'authTry'; readonly reqId: string; readonly name: string; readonly clientId: string }
  | {
      readonly t: 'authPin';
      readonly reqId: string;
      readonly name: string;
      readonly clientId: string;
      readonly pin: string;
    }
  | {
      readonly t: 'register';
      readonly reqId: string;
      readonly name: string;
      readonly clientId: string;
      readonly pin: string;
    }
  | {
      readonly t: 'rename';
      readonly reqId: string;
      readonly from: string;
      readonly to: string;
      readonly clientId: string;
    }
  | {
      readonly t: 'setPin';
      readonly reqId: string;
      readonly name: string;
      readonly clientId: string;
      readonly pin: string;
    }
  | {
      readonly t: 'authResult';
      readonly reqId: string;
      readonly status: AuthStatus;
      readonly stats?: MatchStats;
      readonly attemptsLeft?: number;
      readonly retryAfterMs?: number;
      readonly legacyNoPin?: boolean;
    }
  | { readonly t: 'statsRemove'; readonly name: string }
  // 管理（サーバー権威・§14）。
  | {
      readonly t: 'admin';
      readonly reqId: string;
      readonly op: 'list' | 'delete' | 'edit';
      readonly pw: string;
      readonly name?: string;
      readonly patch?: AdminPatch;
    }
  | {
      readonly t: 'adminResult';
      readonly reqId: string;
      readonly ok: boolean;
      readonly entries?: readonly AdminUser[];
      readonly attemptsLeft?: number;
      readonly locked?: boolean;
      readonly retryAfterMs?: number;
      readonly error?: string;
    }
  // clientId はサーバー側で acct.clientIds と突合し認可（揮発 connName に依存しない＝
  // PartySocket 自動再接続/Durable Object 再起動でも勝敗が確実に記録される）。
  | {
      readonly t: 'reportResult';
      readonly name: string;
      readonly win: boolean;
      readonly clientId: string;
      readonly targetWins?: 1 | 3 | 5 | 9;
      readonly racksPlayed?: number;
    }
  // インベントリ・CPU撃破イベント・装備変更・取得（サーバー権威）。
  | {
      readonly t: 'cpuDefeat';
      readonly name: string;
      readonly clientId: string;
      readonly cpuId: string;
      readonly targetWins: 1 | 3 | 5 | 9;
      readonly perfect: boolean;
      readonly myBreakRuns?: number;
    }
  | {
      readonly t: 'drillClear';
      readonly name: string;
      readonly clientId: string;
      readonly drillId: string;
      readonly rank: string;
      readonly noMiss: boolean;
    }
  | {
      readonly t: 'inventoryEquip';
      readonly name: string;
      readonly clientId: string;
      readonly category: string;
      readonly itemId: string | null;
    }
  | { readonly t: 'inventoryGet'; readonly reqId: string; readonly name: string }
  | {
      readonly t: 'inventoryResult';
      readonly reqId: string;
      readonly found: boolean;
      readonly name: string;
      readonly inventory?: InventorySnapshot;
    }
  | {
      readonly t: 'inventoryUpdate';
      readonly name: string;
      readonly inventory: InventorySnapshot;
      readonly grantedIds: readonly string[];
    }
  | { readonly t: 'statsUpdate'; readonly name: string; readonly stats: MatchStats }
  // 永続成績の全件取得（サーバー権威・pool-lobby ルームのみ）。
  | { readonly t: 'leaderboard'; readonly reqId: string }
  | {
      readonly t: 'leaderboardResult';
      readonly reqId: string;
      readonly entries: readonly LeaderboardEntry[];
    }
  // 個人分析（誰でも read 可・write は clientId 認可）。
  | {
      readonly t: 'shotRecord';
      readonly name: string;
      readonly clientId: string;
      readonly rec: ShotRecord;
    }
  | { readonly t: 'analyticsGet'; readonly reqId: string; readonly name: string }
  | {
      readonly t: 'analyticsResult';
      readonly reqId: string;
      readonly found: boolean;
      readonly name: string;
      readonly totals?: AnalyticsTotals;
      readonly daily?: readonly AnalyticsBucket[];
    }
  // ---- §7A/§7B 指名マッチ＋ラック設定ネゴ（Step 3 でサーバー実装・型のみ Step 2 で先行追加） ----
  // クライアント→サーバー（or 集約役）方向。
  | {
      readonly t: 'requestMatch';
      readonly proposerId: string;
      readonly proposerNickname: string; // pickAccepted で相手に opponent を組み立てる際に必要
      readonly targetId: string;
      readonly raceA: RaceConfig;
    }
  | { readonly t: 'raceCancel'; readonly matchId: string; readonly byUserId: string }
  | {
      readonly t: 'raceUpdate';
      readonly matchId: string;
      readonly race: RaceConfig;
      readonly byUserId: string;
      readonly accepted: boolean;
    }
  | { readonly t: 'raceAccept'; readonly matchId: string; readonly byUserId: string }
  // サーバー→クライアント方向（broadcast）。
  | {
      readonly t: 'pickAccepted';
      readonly matchId: string;
      readonly proposerId: string;
      readonly targetId: string;
      readonly opponentFor: 'proposer' | 'target'; // 受信側がどちらの視点で opponent を見るか
      readonly opponent: LobbyUser;
      readonly raceA: RaceConfig;
      readonly hostId: string;
    }
  | { readonly t: 'pickFailed'; readonly proposerId: string; readonly reason: PickFailReason }
  | {
      readonly t: 'raceUpdated';
      readonly matchId: string;
      readonly race: RaceConfig;
      readonly byUserId: string;
      readonly accepted: boolean;
    }
  | {
      readonly t: 'raceStart';
      readonly matchId: string;
      readonly race: RaceConfig;
      readonly hostId: string;
      readonly proposerId: string;
      readonly targetId: string;
      readonly hostUser: LobbyUser;
      readonly guestUser: LobbyUser;
    }
  | {
      readonly t: 'raceAborted';
      readonly matchId: string;
      readonly proposerId: string;
      readonly targetId: string;
      readonly reason: 'cancelled' | 'disconnected' | 'leftLobby';
      readonly byUserId?: string;
    }
  // ロビーチャット（ラック設定モーダル中の対話用・broadcast のみで履歴/集約なし）。
  // voice は受信側読み上げ用の送信者プロファイル（任意・後方互換）。
  | {
      readonly t: 'lobbyChat';
      readonly matchId: string;
      readonly text: string;
      readonly byUserId: string;
      readonly byNickname: string;
      readonly voice?: { readonly pitch: number; readonly rate: number; readonly gender: 'female' | 'male' };
    };

const AUTH_TIMEOUT_MS = 4000; // 認証応答待ちの上限（超過時はローカル扱い）
const LEADERBOARD_TIMEOUT_MS = 4000; // 順位表取得待ちの上限（超過時は空配列）
const ANALYTICS_TIMEOUT_MS = 4000; // 個人プロファイル取得待ちの上限（超過時は null）

/** ブラウザ永続の安定クライアントID（再読み込み/再接続でも不変）。
 *  名前重複判定で「自分の古い接続」と衝突しても自分の名前を引き取るために使う。 */
const CLIENT_ID_KEY = 'pool.clientId';
// PIN無しログイン/PIN再設定の認可根拠になるため、推測困難な値を使う（crypto.randomUUID 優先）。
const genClientId = (): string => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return `c${crypto.randomUUID()}`;
    }
  } catch {
    /* fallthrough */
  }
  // フォールバック（古環境のみ）: getRandomValues で 128bit。
  try {
    const a = new Uint8Array(16);
    crypto.getRandomValues(a);
    return `c${[...a].map((b) => b.toString(16).padStart(2, '0')).join('')}`;
  } catch {
    return `c${Math.floor(Math.random() * 1e12).toString(36)}`;
  }
};
const loadOrCreateClientId = (): string => {
  try {
    const v = localStorage.getItem(CLIENT_ID_KEY);
    if (v && v.length > 0) return v;
    const id = genClientId();
    localStorage.setItem(CLIENT_ID_KEY, id);
    return id;
  } catch {
    // localStorage 不可環境ではセッション内のみ一意（永続なし）。
    return genClientId();
  }
};

/** ロビーチャンネルのルーム名（バックエンド共通）。 */
const LOBBY_ROOM = 'pool-lobby';
const HEARTBEAT_MS = 2000;
const STALE_MS = 7000;

/**
 * 任意の MsgChannel 上にロビーを構築する（loopback / PartyKit 共通）。
 * serverBacked=true（PartyKit）のとき claimName/reportResult をサーバー権威で処理する。
 * false（ループバック）は名前重複/成績共有を行わず 'local' を返す（呼び出し側がローカル成績）。
 */
export const createChannelLobby = (
  ch: MsgChannel<LobbyMsg>,
  serverBacked: boolean,
): LobbyClient => {
  const userId = `u${Math.floor(Math.random() * 1e9)}`;
  const clientId = loadOrCreateClientId();
  let authSeq = 0;
  const pendingAuth = new Map<string, (r: AuthResult) => void>();
  let adminSeq = 0;
  const pendingAdmin = new Map<string, (r: AdminResult) => void>();
  let lbSeq = 0;
  const pendingLeaderboards = new Map<
    string,
    (entries: readonly LeaderboardEntry[]) => void
  >();
  let analySeq = 0;
  const pendingAnalytics = new Map<string, (r: AnalyticsResponse | null) => void>();
  let invSeq = 0;
  const pendingInventory = new Map<string, (r: InventorySnapshot | null) => void>();
  let inventoryUpdateCb: ((u: InventoryUpdate) => void) | null = null;
  let statsCb: ((name: string, stats: MatchStats) => void) | null = null;
  let statsRemoveCb: ((name: string) => void) | null = null;
  let me: LobbyUser = {
    userId,
    nickname: 'Player',
    status: 'idle',
    matchId: null,
  };
  const seen = new Map<string, { user: LobbyUser; ts: number }>();
  // 相手 nickname の roster 補完: relay は相手の実 nickname を知らないとき userId で仮置きして
  // 送る契約（party/relay.ts の broadcastPickAccepted / broadcastRaceStart 参照）。
  // 受信側で在席情報（seen）から実ニックネームへ補完する。見つからなければそのまま返す。
  const resolveOpponentNickname = (user: LobbyUser): LobbyUser => {
    if (user.nickname !== user.userId) return user; // 仮置きでなければそのまま
    const known = seen.get(user.userId);
    return known ? { ...user, nickname: known.user.nickname } : user;
  };
  let joined = false; // ニックネーム確定前は在席を公開しない
  let rosterCb: ((r: Roster) => void) | null = null;
  let matchedCb: ((m: MatchAssignment) => void) | null = null;
  // §7A/§7B 指名マッチ＋ラック設定ネゴ用コールバック（Step 3 で実装・現状は no-op スタブ）。
  // 各コールバックは保持のみ・送信ロジックは Step 3 でサーバー実装と合わせて追加する。
  let pickAcceptedCb: ((p: RaceProposal) => void) | null = null;
  let pickFailedCb: ((r: PickFailReason) => void) | null = null;
  let raceUpdatedCb: ((u: RaceUpdated) => void) | null = null;
  let raceStartCb: ((s: RaceStartInfo) => void) | null = null;
  let raceAbortedCb: ((a: RaceAborted) => void) | null = null;
  // ロビーチャット（ラック設定モーダル中の対話・broadcast のみ・履歴/集約不要）。
  let lobbyChatCb:
    | ((
        matchId: string,
        byUserId: string,
        byNickname: string,
        text: string,
        voice?: { readonly pitch: number; readonly rate: number; readonly gender: 'female' | 'male' },
      ) => void)
    | null = null;

  // ---- §7A/§7B ProposalState（集約役・最小 userId のタブのみが保持） ----
  // 構造は party/relay.ts と同一。loopback では「最小 userId の人間タブ」が arbiter となり調停する。
  // serverBacked=true（PartyKit）時はサーバーが調停するため、ここのロジックは無効化される（serverBacked 分岐で skip）。
  interface ProposalState {
    readonly matchId: string;
    readonly proposerId: string;
    readonly proposerNickname: string;
    readonly targetId: string;
    raceA: RaceConfig;
    raceB: RaceConfig;
    acceptedA: boolean;
    acceptedB: boolean;
    readonly createdAt: number;
  }
  const proposals = new Map<string, ProposalState>(); // matchId → state
  const proposalByUserId = new Map<string, string>(); // userId → matchId
  const PROPOSAL_STALE_MS = 60_000;

  const isHumanArbiter = (): boolean => {
    const roster = buildRoster();
    const humans = roster.filter((u) => !isCpuUserId(u.userId));
    const minId = humans.map((u) => u.userId).sort()[0];
    return minId === userId;
  };

  const gcStaleProposals = (): void => {
    const t = now();
    for (const [matchId, st] of proposals) {
      if (t - st.createdAt > PROPOSAL_STALE_MS) {
        proposals.delete(matchId);
        proposalByUserId.delete(st.proposerId);
        proposalByUserId.delete(st.targetId);
        // GC で消えた proposal は両者に raceAborted を通知（自分・相手とも）。
        broadcastAborted({
          matchId,
          proposerId: st.proposerId,
          targetId: st.targetId,
          reason: 'disconnected',
        });
      }
    }
  };

  const raceConfigEqual = (a: RaceConfig, b: RaceConfig): boolean =>
    a.targetWins === b.targetWins && a.breakRule === b.breakRule;

  const genMatchId = (): string => `p${Math.floor(Math.random() * 0xffffffff).toString(16).padStart(8, '0')}`;

  // ---- broadcast ヘルパ: 自分が当事者ならローカルコールバックも直接呼ぶ（BroadcastChannel は自エコーしないため） ----

  const broadcastPickAccepted = (st: ProposalState): void => {
    const targetUser =
      buildRoster().find((u) => u.userId === st.targetId) ??
      ({ userId: st.targetId, nickname: st.targetId, status: 'waiting', matchId: null } satisfies LobbyUser);
    const proposerUser: LobbyUser = {
      userId: st.proposerId,
      nickname: st.proposerNickname,
      status: 'waiting',
      matchId: null,
    };
    // proposer 視点（opponent=target）と target 視点（opponent=proposer）の 2 回送る。
    const msgForProposer = {
      t: 'pickAccepted' as const,
      matchId: st.matchId,
      proposerId: st.proposerId,
      targetId: st.targetId,
      opponentFor: 'proposer' as const,
      opponent: targetUser,
      raceA: st.raceA,
      hostId: st.proposerId,
    };
    const msgForTarget = {
      t: 'pickAccepted' as const,
      matchId: st.matchId,
      proposerId: st.proposerId,
      targetId: st.targetId,
      opponentFor: 'target' as const,
      opponent: proposerUser,
      raceA: st.raceA,
      hostId: st.proposerId,
    };
    try {
      ch.post(msgForProposer);
      ch.post(msgForTarget);
    } catch (err) {
      console.error('lobby pickAccepted送信失敗:', err);
    }
    // 自分が当事者ならローカルでコールバックを直接呼ぶ。
    if (pickAcceptedCb) {
      if (userId === st.proposerId) {
        pickAcceptedCb({
          matchId: st.matchId,
          opponent: targetUser,
          raceA: st.raceA,
          hostId: st.proposerId,
        });
      } else if (userId === st.targetId) {
        pickAcceptedCb({
          matchId: st.matchId,
          opponent: proposerUser,
          raceA: st.raceA,
          hostId: st.proposerId,
        });
      }
    }
  };

  const broadcastPickFailed = (proposerId: string, reason: PickFailReason): void => {
    try {
      ch.post({ t: 'pickFailed', proposerId, reason });
    } catch (err) {
      console.error('lobby pickFailed送信失敗:', err);
    }
    if (userId === proposerId && pickFailedCb) {
      pickFailedCb(reason);
    }
  };

  const broadcastRaceUpdated = (
    matchId: string,
    race: RaceConfig,
    byUserId: string,
    accepted: boolean,
  ): void => {
    try {
      ch.post({ t: 'raceUpdated', matchId, race, byUserId, accepted });
    } catch (err) {
      console.error('lobby raceUpdated送信失敗:', err);
    }
    if (raceUpdatedCb) raceUpdatedCb({ matchId, race, byUserId, accepted });
  };

  const broadcastRaceStart = (st: ProposalState): void => {
    const targetUser =
      buildRoster().find((u) => u.userId === st.targetId) ??
      ({ userId: st.targetId, nickname: st.targetId, status: 'playing', matchId: st.matchId } satisfies LobbyUser);
    const proposerUser: LobbyUser = {
      userId: st.proposerId,
      nickname: st.proposerNickname,
      status: 'playing',
      matchId: st.matchId,
    };
    // hostId === proposerId （申込側が host）
    const hostUser = proposerUser;
    const guestUser = targetUser;
    try {
      ch.post({
        t: 'raceStart',
        matchId: st.matchId,
        race: st.raceA,
        hostId: st.proposerId,
        proposerId: st.proposerId,
        targetId: st.targetId,
        hostUser,
        guestUser,
      });
    } catch (err) {
      console.error('lobby raceStart送信失敗:', err);
    }
    if (raceStartCb && (userId === st.proposerId || userId === st.targetId)) {
      const opp = userId === st.proposerId ? guestUser : hostUser;
      raceStartCb({
        matchId: st.matchId,
        race: st.raceA,
        hostId: st.proposerId,
        opponent: opp,
      });
    }
  };

  const broadcastAborted = (params: {
    matchId: string;
    proposerId: string;
    targetId: string;
    reason: 'cancelled' | 'disconnected' | 'leftLobby';
    byUserId?: string;
  }): void => {
    try {
      // exactOptionalPropertyTypes 対応: byUserId 省略可。
      const base = {
        t: 'raceAborted' as const,
        matchId: params.matchId,
        proposerId: params.proposerId,
        targetId: params.targetId,
        reason: params.reason,
      };
      ch.post(params.byUserId !== undefined ? { ...base, byUserId: params.byUserId } : base);
    } catch (err) {
      console.error('lobby raceAborted送信失敗:', err);
    }
    if (raceAbortedCb && (userId === params.proposerId || userId === params.targetId)) {
      const out = { matchId: params.matchId, reason: params.reason };
      raceAbortedCb(params.byUserId !== undefined ? { ...out, byUserId: params.byUserId } : out);
    }
  };

  // ---- 集約役の調停（arbiter 側で実行・C→S 方向メッセージの処理本体） ----

  const arbitrateRequestMatch = (
    proposerId: string,
    proposerNickname: string,
    targetId: string,
    raceA: RaceConfig,
  ): void => {
    gcStaleProposals();
    // busy 判定: proposer または target が既に proposal を持っていれば後着。
    if (proposalByUserId.has(proposerId) || proposalByUserId.has(targetId)) {
      broadcastPickFailed(proposerId, 'busy');
      return;
    }
    const matchId = genMatchId();
    const st: ProposalState = {
      matchId,
      proposerId,
      proposerNickname,
      targetId,
      raceA,
      raceB: raceA, // 初期は申込側の値を被申込側にも採用
      acceptedA: false,
      acceptedB: false,
      createdAt: now(),
    };
    proposals.set(matchId, st);
    proposalByUserId.set(proposerId, matchId);
    proposalByUserId.set(targetId, matchId);
    broadcastPickAccepted(st);
  };

  const arbitrateRaceUpdate = (
    matchId: string,
    race: RaceConfig,
    byUserId: string,
    accepted: boolean,
  ): void => {
    const st = proposals.get(matchId);
    if (!st) return; // 古い・破棄済み
    if (byUserId === st.proposerId) {
      // 相手の承諾リセット条件は「新 race が相手の race と一致しない」時のみ。
      // 一致するなら「相手の提案を受け入れた（承認）」意図なので相手の承諾を維持する
      // （A が新値で再提案 → B が承認ボタンで同値を accepted=true 送信 → 相手承諾維持 → 揃って raceStart）。
      const matchesOther = raceConfigEqual(race, st.raceB);
      st.raceA = race;
      st.acceptedA = accepted;
      if (!matchesOther) st.acceptedB = false;
    } else if (byUserId === st.targetId) {
      const matchesOther = raceConfigEqual(race, st.raceA);
      st.raceB = race;
      st.acceptedB = accepted;
      if (!matchesOther) st.acceptedA = false;
    } else {
      return; // 当事者外
    }
    broadcastRaceUpdated(matchId, race, byUserId, accepted);
    if (st.acceptedA && st.acceptedB && raceConfigEqual(st.raceA, st.raceB)) {
      proposals.delete(matchId);
      proposalByUserId.delete(st.proposerId);
      proposalByUserId.delete(st.targetId);
      broadcastRaceStart(st);
    }
  };

  const arbitrateRaceAccept = (matchId: string, byUserId: string): void => {
    const st = proposals.get(matchId);
    if (!st) return;
    let accepted = false;
    if (byUserId === st.proposerId) {
      st.acceptedA = true;
      accepted = true;
    } else if (byUserId === st.targetId) {
      st.acceptedB = true;
      accepted = true;
    }
    if (!accepted) return;
    // accepted 通知のみ broadcast（race は不変）。
    const race = byUserId === st.proposerId ? st.raceA : st.raceB;
    broadcastRaceUpdated(matchId, race, byUserId, true);
    if (st.acceptedA && st.acceptedB && raceConfigEqual(st.raceA, st.raceB)) {
      proposals.delete(matchId);
      proposalByUserId.delete(st.proposerId);
      proposalByUserId.delete(st.targetId);
      broadcastRaceStart(st);
    }
  };

  const arbitrateRaceCancel = (matchId: string, byUserId: string): void => {
    const st = proposals.get(matchId);
    if (!st) return;
    proposals.delete(matchId);
    proposalByUserId.delete(st.proposerId);
    proposalByUserId.delete(st.targetId);
    broadcastAborted({
      matchId,
      proposerId: st.proposerId,
      targetId: st.targetId,
      reason: 'cancelled',
      byUserId,
    });
  };

  const now = (): number => Date.now();

  // CPU 仮想ユーザー（複数）。在席人数（自分以外の人間）が 0 のときのみロビーに登場し、
  // 他の人間がオンライン化したら roster から消える（既存対戦中には影響しない＝main 側で保持）。
  // 通常の人間対戦と同じ「対戦相手をさがす」操作で CPU と自動ペアリングする（tryPair が特例で結合）。
  // 複数 CPU がいる場合、マッチング時にランダム選出。
  const cpuPresences = (): LobbyUser[] =>
    lobbyCpuPlayers().map((p) => ({
      userId: p.id,
      nickname: p.displayName,
      status: 'waiting', // 常時 '待機中' 表示で「対戦受付中」を明示。matching は tryPair の CPU 特例で結合。
      matchId: null,
      // waitingSince=0 = 最古 → 他の人間 waiting がいない時のみ CPU と即マッチ（tryPair の条件で保証）。
      waitingSince: 0,
      stats: { wins: 0, losses: 0 }, // 表示は controls 側で「— / —」に置換
    }));

  const buildRoster = (): LobbyUser[] => {
    const t = now();
    const list: LobbyUser[] = [me];
    let humansSeen = 0;
    for (const v of seen.values()) {
      if (v.user.userId !== userId && t - v.ts <= STALE_MS) {
        list.push(v.user);
        humansSeen++;
      }
    }
    // 人間 0 人のときのみ CPU 群を追加。
    if (humansSeen === 0) list.push(...cpuPresences());
    return list;
  };
  const emitRoster = (): void => {
    if (rosterCb) rosterCb(buildRoster());
  };
  const broadcastPresence = (): void => {
    if (!joined) return; // 未参加（ニックネーム未確定）は公開しない
    try {
      ch.post({ t: 'presence', user: me });
    } catch (err) {
      console.error('lobby presence送信失敗:', err);
    }
  };

  const handleMatched = (
    matchId: string,
    hostId: string,
    guestId: string,
  ): void => {
    if (me.userId !== hostId && me.userId !== guestId) return;
    const role: 'host' | 'guest' = me.userId === hostId ? 'host' : 'guest';
    const oppId = me.userId === hostId ? guestId : hostId;
    const opp =
      buildRoster().find((u) => u.userId === oppId) ??
      ({ userId: oppId, nickname: '?', status: 'playing', matchId } satisfies LobbyUser);
    if (matchedCb) matchedCb({ matchId, role, opponent: opp });
  };

  // 集約役（最小 userId）のみがペアを決定する（旧・自動マッチメイク）。
  // §7A 指名マッチ移行（2026-06-09）に伴い無効化。本関数は呼び出し元との互換維持のため
  // 関数定義は残すが、中身は no-op。新フローは requestMatch/raceUpdate/raceAccept/raceCancel
  // を arbitrate*** 経由で処理する（ch.onMessage で同等の調停が既に動作している）。
  const tryPair = (): void => {
    // no-op: 自動マッチは §7A 指名マッチに置換済み
  };

  ch.onMessage((msg: LobbyMsg): void => {
    if (msg.t === 'presence') {
      if (msg.user.userId === userId) return;
      seen.set(msg.user.userId, { user: msg.user, ts: now() });
      emitRoster();
      tryPair();
    } else if (msg.t === 'bye') {
      seen.delete(msg.userId);
      emitRoster();
    } else if (msg.t === 'matched') {
      handleMatched(msg.matchId, msg.hostId, msg.guestId);
    } else if (msg.t === 'authResult') {
      const resolve = pendingAuth.get(msg.reqId);
      if (resolve) {
        pendingAuth.delete(msg.reqId);
        // msg は AuthResult を構造的に満たす（t/reqId は余分プロパティとして無害）。
        resolve(msg);
      }
    } else if (msg.t === 'adminResult') {
      const resolve = pendingAdmin.get(msg.reqId);
      if (resolve) {
        pendingAdmin.delete(msg.reqId);
        resolve(msg); // AdminResult を構造的に満たす（t/reqId は無害な余分）
      }
    } else if (msg.t === 'statsRemove') {
      if (statsRemoveCb) statsRemoveCb(msg.name);
    } else if (msg.t === 'statsUpdate') {
      if (statsCb) statsCb(msg.name, msg.stats);
    } else if (msg.t === 'leaderboardResult') {
      const resolve = pendingLeaderboards.get(msg.reqId);
      if (resolve) {
        pendingLeaderboards.delete(msg.reqId);
        resolve(msg.entries);
      }
    } else if (msg.t === 'analyticsResult') {
      const resolve = pendingAnalytics.get(msg.reqId);
      if (resolve) {
        pendingAnalytics.delete(msg.reqId);
        if (msg.found && msg.totals && msg.daily) {
          resolve({ name: msg.name, totals: msg.totals, daily: msg.daily });
        } else {
          resolve(null);
        }
      }
    } else if (msg.t === 'inventoryResult') {
      const resolve = pendingInventory.get(msg.reqId);
      if (resolve) {
        pendingInventory.delete(msg.reqId);
        resolve(msg.found && msg.inventory ? msg.inventory : null);
      }
    } else if (msg.t === 'inventoryUpdate') {
      // 取得 or 装備変更の権威結果。自分の名前のみ反映（他人の inventory は通常配信されないが防御的に）。
      if (inventoryUpdateCb) {
        inventoryUpdateCb({
          name: msg.name,
          inventory: msg.inventory,
          grantedIds: msg.grantedIds,
        });
      }
    } else if (msg.t === 'pickAccepted') {
      // §7A: 送信側（relay / loopback arbiter）は proposer 視点・target 視点の 2 通を
      // broadcast する。`opponentFor` で自分宛の視点のみ消費する。
      // （以前は当事者が 2 通とも消費して二重発火し、申込側の「相手名」が
      //  2 通目＝自分自身の情報で上書きされる表示不具合があった）
      const forMe =
        (msg.opponentFor === 'proposer' && msg.proposerId === userId) ||
        (msg.opponentFor === 'target' && msg.targetId === userId);
      if (forMe && pickAcceptedCb) {
        pickAcceptedCb({
          matchId: msg.matchId,
          opponent: resolveOpponentNickname(msg.opponent),
          raceA: msg.raceA,
          hostId: msg.hostId,
        });
      }
    } else if (msg.t === 'pickFailed') {
      if (msg.proposerId === userId && pickFailedCb) {
        pickFailedCb(msg.reason);
      }
    } else if (msg.t === 'raceUpdated') {
      if (raceUpdatedCb) {
        raceUpdatedCb({
          matchId: msg.matchId,
          race: msg.race,
          byUserId: msg.byUserId,
          accepted: msg.accepted,
        });
      }
    } else if (msg.t === 'raceStart') {
      const isHost = msg.hostId === userId;
      const isGuest = msg.proposerId === userId || msg.targetId === userId;
      if (isGuest && raceStartCb) {
        // 相手側ユーザーの nickname は relay 側で userId 仮置きの場合があるため roster 補完する。
        const opp = resolveOpponentNickname(isHost ? msg.guestUser : msg.hostUser);
        raceStartCb({
          matchId: msg.matchId,
          race: msg.race,
          hostId: msg.hostId,
          opponent: opp,
        });
      }
    } else if (msg.t === 'raceAborted') {
      if ((msg.proposerId === userId || msg.targetId === userId) && raceAbortedCb) {
        // exactOptionalPropertyTypes 対応: byUserId が undefined のときは省略して構築。
        const base = { matchId: msg.matchId, reason: msg.reason };
        raceAbortedCb(msg.byUserId !== undefined ? { ...base, byUserId: msg.byUserId } : base);
      }
    } else if (msg.t === 'lobbyChat') {
      // 自エコー（BroadcastChannel は自身に届かない）想定: 受信は他者の発言のみ。
      // クライアント側で byUserId === myUserId フィルタを掛ける契約のため、ここでは無条件に配信。
      if (lobbyChatCb) lobbyChatCb(msg.matchId, msg.byUserId, msg.byNickname, msg.text, msg.voice);
    }
    // ---- C→S 方向: serverBacked=false（loopback）のみ、arbiter（最小 userId）が調停する ----
    // serverBacked=true（PartyKit）はサーバーが調停するため、ここでは何もしない。
    else if (!serverBacked && msg.t === 'requestMatch') {
      if (!isHumanArbiter()) return;
      arbitrateRequestMatch(msg.proposerId, msg.proposerNickname, msg.targetId, msg.raceA);
    } else if (!serverBacked && msg.t === 'raceUpdate') {
      if (!isHumanArbiter()) return;
      arbitrateRaceUpdate(msg.matchId, msg.race, msg.byUserId, msg.accepted);
    } else if (!serverBacked && msg.t === 'raceAccept') {
      if (!isHumanArbiter()) return;
      arbitrateRaceAccept(msg.matchId, msg.byUserId);
    } else if (!serverBacked && msg.t === 'raceCancel') {
      if (!isHumanArbiter()) return;
      arbitrateRaceCancel(msg.matchId, msg.byUserId);
    }
  });

  const hb = setInterval((): void => {
    broadcastPresence();
    emitRoster();
    tryPair();
  }, HEARTBEAT_MS);

  const update = (next: Partial<LobbyUser>): void => {
    me = { ...me, ...next };
    broadcastPresence();
    emitRoster();
  };

  // 認証リクエスト共通: メッセージ送信 → authResult 待ち（serverBacked のみ。loopback は 'local'）。
  const authRequest = (make: (reqId: string) => LobbyMsg): Promise<AuthResult> => {
    if (!serverBacked) return Promise.resolve({ status: 'local' });
    const reqId = `${userId}-a${++authSeq}`;
    return new Promise<AuthResult>((resolve) => {
      let settled = false;
      const done = (r: AuthResult): void => {
        if (settled) return;
        settled = true;
        pendingAuth.delete(reqId);
        resolve(r);
      };
      pendingAuth.set(reqId, done);
      // serverBacked では応答なし＝認証失敗扱い（fail-closed）。'local' は loopback のみ。
      setTimeout(() => done({ status: 'error' }), AUTH_TIMEOUT_MS);
      try {
        ch.post(make(reqId));
      } catch (err) {
        console.error('lobby auth送信失敗:', err);
        done({ status: 'error' });
      }
    });
  };

  // 管理リクエスト共通（serverBacked のみ。loopback は管理不可）。
  const adminRequest = (make: (reqId: string) => LobbyMsg): Promise<AdminResult> => {
    if (!serverBacked) return Promise.resolve({ ok: false, error: 'disabled' });
    const reqId = `${userId}-adm${++adminSeq}`;
    return new Promise<AdminResult>((resolve) => {
      let settled = false;
      const done = (r: AdminResult): void => {
        if (settled) return;
        settled = true;
        pendingAdmin.delete(reqId);
        resolve(r);
      };
      pendingAdmin.set(reqId, done);
      setTimeout(() => done({ ok: false, error: 'error' }), AUTH_TIMEOUT_MS);
      try {
        ch.post(make(reqId));
      } catch (err) {
        console.error('lobby admin送信失敗:', err);
        done({ ok: false, error: 'error' });
      }
    });
  };

  return {
    userId,
    authTry: (name: string): Promise<AuthResult> =>
      authRequest((reqId) => ({ t: 'authTry', reqId, name, clientId })),
    authPin: (name: string, pin: string): Promise<AuthResult> =>
      authRequest((reqId) => ({ t: 'authPin', reqId, name, clientId, pin })),
    register: (name: string, pin: string): Promise<AuthResult> =>
      authRequest((reqId) => ({ t: 'register', reqId, name, clientId, pin })),
    renameAccount: (from: string, to: string): Promise<AuthResult> =>
      authRequest((reqId) => ({ t: 'rename', reqId, from, to, clientId })),
    setPin: (name: string, pin: string): Promise<AuthResult> =>
      authRequest((reqId) => ({ t: 'setPin', reqId, name, clientId, pin })),
    onStatsRemove: (cb: (name: string) => void): void => {
      statsRemoveCb = cb;
    },
    // loopback（ローカル PC 完結・外部攻撃者は想定外＝security-baseline.md 準拠）では
    // 認証チェックをスキップして管理パネルを開けるようにする。サーバー権威のアカウント一覧は
    // 持たないため entries は空配列を返す（CPU 調整タブ等の UI が利用可能になる）。
    // serverBacked（PartyKit/Cloudflare）時は従来どおりサーバーで ADMIN_PASSWORD 照合。
    adminList: (pw: string): Promise<AdminResult> => {
      if (!serverBacked) return Promise.resolve({ ok: true, entries: [] });
      return adminRequest((reqId) => ({ t: 'admin', reqId, op: 'list', pw }));
    },
    adminDelete: (pw: string, name: string): Promise<AdminResult> => {
      if (!serverBacked) return Promise.resolve({ ok: false, error: 'disabled' });
      return adminRequest((reqId) => ({ t: 'admin', reqId, op: 'delete', pw, name }));
    },
    adminEdit: (pw: string, name: string, patch: AdminPatch): Promise<AdminResult> => {
      if (!serverBacked) return Promise.resolve({ ok: false, error: 'disabled' });
      return adminRequest((reqId) => ({ t: 'admin', reqId, op: 'edit', pw, name, patch }));
    },
    reportResult: (
      name: string,
      win: boolean,
      opts?: { targetWins?: 1 | 3 | 5 | 9; racksPlayed?: number },
    ): void => {
      if (!serverBacked) return; // ローカルは main 側で localStorage 加算
      try {
        // clientId 同梱: PartySocket 再接続/DO 再起動で揮発 connName が失われていても、
        // サーバーは acct.clientIds との突合で報告者を認可できる。
        // targetWins / racksPlayed はサーバー側集計（race 別勝利数・累計ラック数）の補助。
        const tw = opts?.targetWins;
        const rp = opts?.racksPlayed;
        const msg: LobbyMsg = {
          t: 'reportResult',
          name,
          win,
          clientId,
          ...(tw !== undefined ? { targetWins: tw } : {}),
          ...(typeof rp === 'number' ? { racksPlayed: rp } : {}),
        };
        ch.post(msg);
      } catch (err) {
        console.error('lobby reportResult送信失敗:', err);
      }
    },
    reportCpuDefeat: (name: string, event: CpuDefeatEvent): void => {
      // CPU 撃破はサーバー権威で履歴・解錠評価する。ループバック（serverBacked=false）は
      // サーバー成績を持たないためスキップ（CPU 戦の取得物は本来サーバーログイン前提＝OPT:A）。
      if (!serverBacked) return;
      try {
        const msg: LobbyMsg = {
          t: 'cpuDefeat',
          name,
          clientId,
          cpuId: event.cpuId,
          targetWins: event.targetWins,
          perfect: event.perfect,
          ...(typeof event.myBreakRuns === 'number' ? { myBreakRuns: event.myBreakRuns } : {}),
        };
        ch.post(msg);
      } catch (err) {
        console.error('lobby cpuDefeat送信失敗:', err);
      }
    },
    reportDrillClear: (name: string, event: DrillClearEvent): void => {
      // ドリル進捗はログイン時のみサーバー権威。未ログイン/ループバック（serverBacked=false）は
      // ローカル localStorage 権威のためスキップ（drill-progress.ts が保存済み）。
      if (!serverBacked) return;
      try {
        const msg: LobbyMsg = {
          t: 'drillClear',
          name,
          clientId,
          drillId: event.drillId,
          rank: event.rank,
          noMiss: event.noMiss,
        };
        ch.post(msg);
      } catch (err) {
        console.error('lobby drillClear送信失敗:', err);
      }
    },
    equipItem: (name: string, category: string, itemId: string | null): void => {
      if (!serverBacked) return;
      try {
        ch.post({ t: 'inventoryEquip', name, clientId, category, itemId });
      } catch (err) {
        console.error('lobby inventoryEquip送信失敗:', err);
      }
    },
    getInventory: (name: string): Promise<InventorySnapshot | null> => {
      if (!serverBacked) return Promise.resolve(null);
      const reqId = `${userId}-inv${++invSeq}`;
      return new Promise<InventorySnapshot | null>((resolve) => {
        let settled = false;
        const done = (r: InventorySnapshot | null): void => {
          if (settled) return;
          settled = true;
          pendingInventory.delete(reqId);
          resolve(r);
        };
        pendingInventory.set(reqId, done);
        setTimeout(() => done(null), ANALYTICS_TIMEOUT_MS);
        try {
          ch.post({ t: 'inventoryGet', reqId, name });
        } catch (err) {
          console.error('lobby getInventory送信失敗:', err);
          done(null);
        }
      });
    },
    onInventoryUpdate: (cb: (u: InventoryUpdate) => void): void => {
      inventoryUpdateCb = cb;
    },
    reportShot: (name: string, rec: ShotRecord): void => {
      // ループバック（serverBacked=false）では分析の永続を持たない＝送信スキップ。
      if (!serverBacked) return;
      try {
        ch.post({ t: 'shotRecord', name, clientId, rec });
      } catch (err) {
        console.error('lobby reportShot送信失敗:', err);
      }
    },
    getAnalytics: (name: string): Promise<AnalyticsResponse | null> => {
      // ループバックは個人分析の永続を持たないため null（UI 側でフォールバック）。
      if (!serverBacked) return Promise.resolve(null);
      const reqId = `${userId}-an${++analySeq}`;
      return new Promise<AnalyticsResponse | null>((resolve) => {
        let settled = false;
        const done = (r: AnalyticsResponse | null): void => {
          if (settled) return;
          settled = true;
          pendingAnalytics.delete(reqId);
          resolve(r);
        };
        pendingAnalytics.set(reqId, done);
        setTimeout(() => done(null), ANALYTICS_TIMEOUT_MS);
        try {
          ch.post({ t: 'analyticsGet', reqId, name });
        } catch (err) {
          console.error('lobby getAnalytics送信失敗:', err);
          done(null);
        }
      });
    },
    onStats: (cb: (name: string, stats: MatchStats) => void): void => {
      statsCb = cb;
    },
    join: (nickname: string): void => {
      joined = true;
      update({ nickname, status: 'idle', matchId: null });
    },
    setNickname: (name: string): void => {
      update({ nickname: name });
    },
    setStats: (stats: MatchStats): void => {
      // 自己申告の成績を presence に同梱（ループバックの順位表示用）。
      update({ stats: { wins: stats.wins, losses: stats.losses } });
    },
    setWaiting: (on: boolean): void => {
      // 先着順マッチメイク用に waitingSince を記録する（ON 時のみ）。
      // OFF 時は省略＝古い値が残るが、status='idle' になり tryPair の filter
      // (status==='waiting') で除外されるため動作上の影響はない。次回 ON で
      // 必ず Date.now() に更新される。
      if (on) {
        update({ status: 'waiting', matchId: null, waitingSince: Date.now() });
      } else {
        update({ status: 'idle', matchId: null });
      }
      tryPair();
    },
    setPlaying: (matchId: string | null): void => {
      update({ status: matchId ? 'playing' : 'idle', matchId });
    },
    setSpectating: (matchId: string | null): void => {
      update({ status: matchId ? 'spectating' : 'idle', matchId });
    },
    onRoster: (cb: (r: Roster) => void): void => {
      rosterCb = cb;
    },
    onMatched: (cb: (m: MatchAssignment) => void): void => {
      matchedCb = cb;
    },
    // ---- §7A/§7B 指名マッチ＋ラック設定ネゴ（送信メソッド本体） ----
    // CPU 申込はサーバー/集約役を経由せず、クライアント側で短絡処理（pickAccepted をローカルで発火）。
    // 人間申込はサーバー（serverBacked=true）or 集約役（loopback）が調停する。
    requestMatch: (targetId: string, raceA: RaceConfig): void => {
      // CPU 特例（§7A.5）: target が CPU の場合は調停不要・ローカルで即座に pickAccepted を発火する。
      // CPU 戦のラック設定は申込側のみで完結する（被申込側は実体なし）→ updateRaceProposal/acceptRace も
      // ローカル短絡が必要（後述の send メソッドで実装）。
      if (isCpuUserId(targetId)) {
        if (pickAcceptedCb) {
          const cpuUser =
            buildRoster().find((u) => u.userId === targetId) ??
            ({ userId: targetId, nickname: targetId, status: 'waiting', matchId: null } satisfies LobbyUser);
          const matchId = `cpu-${Date.now()}`;
          pickAcceptedCb({ matchId, opponent: cpuUser, raceA, hostId: userId });
        }
        return;
      }
      // 人間申込: arbiter（serverBacked=true ならサーバー / false なら最小 userId のタブ）へ送信。
      const msg = {
        t: 'requestMatch' as const,
        proposerId: userId,
        proposerNickname: me.nickname,
        targetId,
        raceA,
      };
      try {
        ch.post(msg);
      } catch (err) {
        console.error('lobby requestMatch送信失敗:', err);
      }
      // loopback: 自分が arbiter ならローカルで直接処理（BroadcastChannel は自エコーしないため）。
      if (!serverBacked && isHumanArbiter()) {
        arbitrateRequestMatch(userId, me.nickname, targetId, raceA);
      }
    },
    cancelProposal: (matchId: string): void => {
      // CPU 戦（matchId が 'cpu-' プレフィックス）はサーバー/集約役に状態がない → ローカル no-op で OK
      // （申込側のクライアントがモーダルを閉じれば完了）。
      if (matchId.startsWith('cpu-')) return;
      try {
        ch.post({ t: 'raceCancel', matchId, byUserId: userId });
      } catch (err) {
        console.error('lobby raceCancel送信失敗:', err);
      }
      if (!serverBacked && isHumanArbiter()) {
        arbitrateRaceCancel(matchId, userId);
      }
    },
    updateRaceProposal: (matchId: string, race: RaceConfig, accepted: boolean): void => {
      // CPU 戦は申込側のみで完結。relay にも arbiter にも届かないが、UI のリアルタイム更新のため
      // pickAccepted のローカル発火と同じく、updated を自分のコールバックに直接通知する。
      if (matchId.startsWith('cpu-')) {
        if (raceUpdatedCb) raceUpdatedCb({ matchId, race, byUserId: userId, accepted });
        return;
      }
      try {
        ch.post({ t: 'raceUpdate', matchId, race, byUserId: userId, accepted });
      } catch (err) {
        console.error('lobby raceUpdate送信失敗:', err);
      }
      if (!serverBacked && isHumanArbiter()) {
        arbitrateRaceUpdate(matchId, race, userId, accepted);
      }
    },
    acceptRace: (matchId: string): void => {
      // CPU 戦は acceptRace を経由しない規約（main.ts 側で「対戦開始」押下時に直接 startCpuMatch を呼ぶ）。
      // 万一呼ばれても no-op で安全（onRaceStart に opponent 仮値を渡すと CPU 識別が壊れるため）。
      if (matchId.startsWith('cpu-')) return;
      try {
        ch.post({ t: 'raceAccept', matchId, byUserId: userId });
      } catch (err) {
        console.error('lobby raceAccept送信失敗:', err);
      }
      if (!serverBacked && isHumanArbiter()) {
        arbitrateRaceAccept(matchId, userId);
      }
    },
    onPickAccepted: (cb: (p: RaceProposal) => void): void => {
      pickAcceptedCb = cb;
    },
    onPickFailed: (cb: (r: PickFailReason) => void): void => {
      pickFailedCb = cb;
    },
    onRaceUpdated: (cb: (u: RaceUpdated) => void): void => {
      raceUpdatedCb = cb;
    },
    onRaceStart: (cb: (s: RaceStartInfo) => void): void => {
      raceStartCb = cb;
    },
    onRaceAborted: (cb: (a: RaceAborted) => void): void => {
      raceAbortedCb = cb;
    },
    sendLobbyChat: (
      matchId: string,
      text: string,
      voice?: { readonly pitch: number; readonly rate: number; readonly gender: 'female' | 'male' },
    ): void => {
      // CPU 戦は相手不在＝送信先なし。no-op で安全に終わらせる。
      if (matchId.startsWith('cpu-')) return;
      try {
        // exactOptionalPropertyTypes 対応: voice 未指定時は省略してポスト。
        const base = { t: 'lobbyChat' as const, matchId, text, byUserId: userId, byNickname: me.nickname };
        ch.post(voice !== undefined ? { ...base, voice } : base);
      } catch (err) {
        console.error('lobby lobbyChat送信失敗:', err);
      }
    },
    onLobbyChat: (
      cb: (
        matchId: string,
        byUserId: string,
        byNickname: string,
        text: string,
        voice?: { readonly pitch: number; readonly rate: number; readonly gender: 'female' | 'male' },
      ) => void,
    ): void => {
      lobbyChatCb = cb;
    },
    requestLeaderboard: (): Promise<readonly LeaderboardEntry[]> => {
      // サーバー非利用（ループバック）は永続名簿なし → 空配列（順位表は在席ロスターの自己申告 stats から合成）。
      if (!serverBacked) return Promise.resolve([]);
      const reqId = `${userId}-lb${++lbSeq}`;
      return new Promise<readonly LeaderboardEntry[]>((resolve) => {
        let settled = false;
        const done = (entries: readonly LeaderboardEntry[]): void => {
          if (settled) return;
          settled = true;
          pendingLeaderboards.delete(reqId);
          resolve(entries);
        };
        pendingLeaderboards.set(reqId, done);
        // 応答なし（サーバー不調等）は空配列で続行（ゲームを止めない）。
        setTimeout(() => done([]), LEADERBOARD_TIMEOUT_MS);
        try {
          ch.post({ t: 'leaderboard', reqId });
        } catch (err) {
          console.error('lobby leaderboard送信失敗:', err);
          done([]);
        }
      });
    },
    close: (): void => {
      clearInterval(hb);
      try {
        ch.post({ t: 'bye', userId });
      } catch (err) {
        console.error('lobby bye送信失敗:', err);
      }
      ch.close();
    },
  };
};

/** チャンネルファクトリ（バックエンド）を指定してロビーを生成する。
 *  serverBacked=true（PartyKit）でサーバー権威の名前/成績機能を有効化。 */
export const createLobbyWith = (
  factory: ChannelFactory,
  serverBacked: boolean,
): LobbyClient => createChannelLobby(factory<LobbyMsg>(LOBBY_ROOM), serverBacked);

/** ループバックロビー（同一ブラウザの多タブ間・インフラ不要の検証用）。 */
export const createLoopbackLobby = (): LobbyClient =>
  createLobbyWith(broadcastChannelFactory, false);
